import type { DailyNoteCategoryRow, DailyNoteCategorySystemKey } from '../types/dailyRecordNote';
import {
  DEFAULT_DAILY_CATEGORY_COLOR,
  DEFAULT_DAILY_CATEGORY_NAME,
  LEGACY_GENERAL_DAILY_CATEGORY_NAME,
  STICKY_SYSTEM_CATEGORY_COLOR,
  STICKY_SYSTEM_CATEGORY_NAME,
} from '../types/dailyRecordNote';

export function normalizeDailyCategoryName(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

function categoryNameKey(name: string): string {
  return normalizeDailyCategoryName(name).toLowerCase();
}

export type DailyNoteSystemCategorySeed = {
  key: DailyNoteCategorySystemKey;
  name: string;
  color: string;
  sort_order: number;
};

export const DAILY_NOTE_SYSTEM_CATEGORY_SEEDS: readonly DailyNoteSystemCategorySeed[] = [
  { key: 'general', name: DEFAULT_DAILY_CATEGORY_NAME, color: DEFAULT_DAILY_CATEGORY_COLOR, sort_order: -2 },
  { key: 'sticky', name: STICKY_SYSTEM_CATEGORY_NAME, color: STICKY_SYSTEM_CATEGORY_COLOR, sort_order: -1 },
];

export function isDailyNoteSystemKey(value: unknown): value is DailyNoteCategorySystemKey {
  return value === 'general' || value === 'daily' || value === 'sticky';
}

function isLegacyGeneralName(name: string): boolean {
  return categoryNameKey(name) === categoryNameKey(LEGACY_GENERAL_DAILY_CATEGORY_NAME);
}

function isDefaultDailyName(name: string): boolean {
  const n = categoryNameKey(name);
  return n === categoryNameKey(DEFAULT_DAILY_CATEGORY_NAME) || isLegacyGeneralName(name);
}

export function dailyCategorySystemKey(cat: {
  system_key?: string | null;
  name: string;
}): DailyNoteCategorySystemKey | null {
  if (isDailyNoteSystemKey(cat.system_key)) return cat.system_key;
  if (isDefaultDailyName(cat.name)) return 'general';
  if (categoryNameKey(cat.name) === categoryNameKey(STICKY_SYSTEM_CATEGORY_NAME)) return 'sticky';
  return null;
}

export function isGeneralDailyCategory(cat: { system_key?: string | null; name: string }): boolean {
  if (cat.system_key === 'daily') return false;
  return dailyCategorySystemKey(cat) === 'general';
}

/** 흰 점 테두리용. 기본 데일리(구 일반) 이름 */
export function isDefaultDailyCategoryName(name: string): boolean {
  return isDefaultDailyName(name);
}

export function isBuiltInDailyCategory(cat: { system_key?: string | null; name: string }): boolean {
  return dailyCategorySystemKey(cat) != null;
}

export function canEditDailyCategoryColor(cat: { system_key?: string | null; name: string }): boolean {
  const key = dailyCategorySystemKey(cat);
  return key !== 'general' && key !== 'daily';
}

export function planBuiltInDailyCategories(params: {
  categories: DailyNoteCategoryRow[];
  userId: string;
  generateId: () => string;
  nowIso: string;
}): {
  categories: DailyNoteCategoryRow[];
  inserts: DailyNoteCategoryRow[];
  patches: DailyNoteCategoryRow[];
  removes: DailyNoteCategoryRow[];
} {
  const next = params.categories.map((c) => ({ ...c }));
  const inserts: DailyNoteCategoryRow[] = [];
  const patches: DailyNoteCategoryRow[] = [];
  const removes: DailyNoteCategoryRow[] = [];

  const markPatch = (row: DailyNoteCategoryRow) => {
    row.updated_at = params.nowIso;
    if (!patches.some((p) => p.id === row.id) && !inserts.some((p) => p.id === row.id)) {
      patches.push(row);
    }
  };

  let general =
    next.find((c) => c.system_key === 'general') ??
    next.find((c) => !c.system_key && isLegacyGeneralName(c.name));
  const extraDaily = next.find((c) => c.system_key === 'daily' && c.id !== general?.id);

  if (!general && extraDaily) {
    extraDaily.system_key = 'general';
    extraDaily.name = DEFAULT_DAILY_CATEGORY_NAME;
    extraDaily.color = DEFAULT_DAILY_CATEGORY_COLOR;
    markPatch(extraDaily);
    general = extraDaily;
  } else if (general && extraDaily) {
    removes.push(extraDaily);
    const idx = next.findIndex((c) => c.id === extraDaily.id);
    if (idx >= 0) next.splice(idx, 1);
  }

  if (general) {
    if (general.system_key !== 'general') {
      general.system_key = 'general';
      markPatch(general);
    }
    if (isLegacyGeneralName(general.name)) {
      general.name = DEFAULT_DAILY_CATEGORY_NAME;
      markPatch(general);
    }
    if (general.color.toLowerCase() !== DEFAULT_DAILY_CATEGORY_COLOR) {
      general.color = DEFAULT_DAILY_CATEGORY_COLOR;
      markPatch(general);
    }
  }

  for (const seed of DAILY_NOTE_SYSTEM_CATEGORY_SEEDS) {
    const byKey = next.find((c) => c.system_key === seed.key);
    const byName = next.find(
      (c) => !c.system_key && categoryNameKey(c.name) === categoryNameKey(seed.name)
    );
    const existing = byKey ?? byName;
    if (existing) {
      if (existing.system_key !== seed.key) {
        existing.system_key = seed.key;
        markPatch(existing);
      }
      continue;
    }
    const maxOrder = next.length ? Math.max(...next.map((c) => c.sort_order)) : seed.sort_order - 1;
    const row: DailyNoteCategoryRow = {
      id: params.generateId(),
      user_id: params.userId,
      name: seed.name,
      color: seed.color,
      sort_order: maxOrder + 1,
      created_at: params.nowIso,
      updated_at: params.nowIso,
      system_key: seed.key,
    };
    next.push(row);
    inserts.push(row);
  }

  return {
    categories: next.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name) || a.id.localeCompare(b.id)),
    inserts,
    patches,
    removes,
  };
}

export function applyDailyCategoryModalConfirm(params: {
  categories: DailyNoteCategoryRow[];
  payload: { name: string; color: string; categoryId?: string | null };
  userId: string;
  generateId: () => string;
  nowIso: string;
}): DailyNoteCategoryRow[] | null {
  const normalizedName = normalizeDailyCategoryName(params.payload.name);
  const color = params.payload.color;
  const editId = params.payload.categoryId?.trim() || null;
  const existingById = editId ? params.categories.find((c) => c.id === editId) : undefined;
  const existingByName = params.categories.find(
    (c) => categoryNameKey(c.name) === categoryNameKey(normalizedName)
  );

  if (existingById) {
    if (!normalizedName) return null;
    if (existingByName && existingByName.id !== existingById.id) return null;
    const nextColor = canEditDailyCategoryColor(existingById) ? color : existingById.color;
    return params.categories.map((c) =>
      c.id === existingById.id
        ? { ...c, name: normalizedName, color: nextColor, updated_at: params.nowIso }
        : c
    );
  }

  if (existingByName) {
    const nextColor = canEditDailyCategoryColor(existingByName) ? color : existingByName.color;
    return params.categories.map((c) =>
      c.id === existingByName.id ? { ...c, color: nextColor, updated_at: params.nowIso } : c
    );
  }

  if (!normalizedName) return null;

  const nextOrder = params.categories.length
    ? Math.max(...params.categories.map((c) => c.sort_order)) + 1
    : 0;
  const row: DailyNoteCategoryRow = {
    id: params.generateId(),
    user_id: params.userId,
    name: normalizedName,
    color,
    sort_order: nextOrder,
    created_at: params.nowIso,
    updated_at: params.nowIso,
    system_key: null,
  };
  return [...params.categories, row].sort(
    (a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)
  );
}

export function joinDailyNoteBodies(bodies: string[]): string {
  return bodies.join('\n\n');
}
